/**
 * Estabelecimento Combobox Component
 */

import { useState, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Estabelecimento } from "@/types/estabelecimento";
import { estabelecimentosAdapter } from "@/data";

interface EstabelecimentoComboboxProps {
  value?: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
}

export function EstabelecimentoCombobox({
  value,
  onValueChange,
  disabled = false,
}: EstabelecimentoComboboxProps) {
  const [open, setOpen] = useState(false);
  const [estabelecimentos, setEstabelecimentos] = useState<Estabelecimento[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEstabelecimentos();
  }, []);

  const loadEstabelecimentos = async () => {
    try {
      setLoading(true);
      const response = await estabelecimentosAdapter.list();
      setEstabelecimentos(response.items.filter((e) => e.ativo));
    } catch (error) {
      console.error("Erro ao carregar estabelecimentos:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedEstabelecimento = estabelecimentos.find(
    (e) => e.id_estabelecimento === value
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled || loading}
        >
          {loading
            ? "Carregando..."
            : selectedEstabelecimento
            ? selectedEstabelecimento.nome
            : "Selecione um estabelecimento..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder="Buscar estabelecimento..." />
          <CommandList>
            <CommandEmpty>Nenhum estabelecimento encontrado.</CommandEmpty>
            <CommandGroup>
              {estabelecimentos.map((estabelecimento) => (
                <CommandItem
                  key={estabelecimento.id_estabelecimento}
                  value={estabelecimento.nome}
                  onSelect={() => {
                    onValueChange(estabelecimento.id_estabelecimento);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === estabelecimento.id_estabelecimento
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {estabelecimento.nome}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
